import type { PlayableItem } from "@/lib/player/playable-item"
import type { TrackPrompt } from "@/types"

// Pure derivation for the suggestion feed: the wire ships suggestions
// nested under their prompts (slot-shaped); the feed flattens them into
// the two zones the collaboration reads as — the playlist taking shape,
// and the live stream of fresh suggestions. Rejected suggestions stay on
// the wire but are omitted here: a public "rejected" tag on a
// lived-experience contribution is exactly the homework-portal feel this
// page is killing.

export type FeedTone = "curator" | "accepted" | "pending"

export interface FeedEntry {
  key: string
  tone: FeedTone
  playable: PlayableItem | null
  contributorName: string
  contributorAvatarUrl: string | null
  contributorSlug: string | null
  notes: string | null
  createdAt: string | null
  url: string | null
  prompt: TrackPrompt
}

export interface Feed {
  picks: FeedEntry[]
  fresh: FeedEntry[]
}

export function buildFeed(prompts: TrackPrompt[]): Feed {
  const picks = prompts
    .filter((p) => p.status.state === "locked" || p.status.state === "accepted")
    .sort((a, b) => a.position - b.position)
    .map(pickEntry)

  const fresh = prompts
    .flatMap((prompt) =>
      prompt.track_suggestions
        .filter((s) => s.status === "pending")
        .map(
          (s): FeedEntry => ({
            key: `suggestion-${s.id}`,
            tone: "pending",
            playable: s.playable ?? null,
            contributorName: s.contributor_name,
            contributorAvatarUrl: s.contributor_avatar_url,
            contributorSlug: s.contributor_slug,
            notes: s.notes,
            createdAt: s.created_at,
            url: s.url ?? s.post?.canonical_url ?? null,
            prompt,
          }),
        ),
    )
    .sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""))

  return { picks, fresh }
}

function pickEntry(prompt: TrackPrompt): FeedEntry {
  if (prompt.status.state === "accepted") {
    const accepted = prompt.track_suggestions.find(
      (s) => s.status === "accepted",
    )
    return {
      key: `prompt-${prompt.id}-accepted`,
      tone: "accepted",
      playable: accepted?.playable ?? null,
      contributorName:
        accepted?.contributor_name ??
        prompt.status.accepted_contributor_name ??
        "A contributor",
      contributorAvatarUrl: accepted?.contributor_avatar_url ?? null,
      contributorSlug: accepted?.contributor_slug ?? null,
      notes: accepted?.notes ?? null,
      createdAt: accepted?.created_at ?? null,
      url: accepted?.url ?? accepted?.post?.canonical_url ?? null,
      prompt,
    }
  }

  // Locked: the curator filled the slot directly. The playable projection
  // ships as `curator_playable`; a severed track (data edge) degrades to a
  // metadata-only entry.
  return {
    key: `prompt-${prompt.id}-curator`,
    tone: "curator",
    playable: prompt.curator_playable ?? null,
    contributorName: prompt.status.accepted_contributor_name ?? "Curator",
    contributorAvatarUrl: null,
    contributorSlug: null,
    notes: prompt.curator_playable?.notes ?? null,
    createdAt: null,
    url: prompt.curator_playable?.source_url ?? null,
    prompt,
  }
}
